import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";
import { Card, CardBody, Row, Col } from "reactstrap";
import { reportsApi } from "../../services/reportsServices";
import { utils } from "../../utils/utils";

ChartJS.register(ArcElement, Tooltip);

const EXCLUDED_LABOR_CODES = ["14", "18"];
const statusId = 4;

const FabricationHoursChart = () => {
  const [fabricationData, setFabricationData] = useState(null);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const result = await reportsApi.manHoursByLaborCode({ statusId });
        if (
          result &&
          result.data &&
          result.data &&
          result.data.laborCategories.length > 0
        ) {
          const allLaborCodes = result.data?.laborCategories?.map((item) => {
            const itemDone = item.totalManHours || 0;
            const itemEstimated = item.totalEstimatedHours || 0;
            return {
              lc: item.code,
              done: utils.roundBasedOnThirdDecimalDigit(itemDone),
              rem: utils.roundBasedOnThirdDecimalDigit(
                itemEstimated - itemDone
              ),
            };
          });
          const done = utils.roundBasedOnThirdDecimalDigit(
            result.data.totalManHours
          );
          const estimated = utils.roundBasedOnThirdDecimalDigit(
            result.data.totalEstimatedHours
          );

          const fabricationLaborCodes = allLaborCodes.filter((lc) =>
            EXCLUDED_LABOR_CODES.includes(lc.lc)
          );

          const totalFabricationHours = {
            total: utils.roundBasedOnThirdDecimalDigit(
              fabricationLaborCodes.reduce((acc, curr) => acc + curr.rem, 0)
            ),
            laborCodes: fabricationLaborCodes,
          };

          setFabricationData({
            done: done,
            estimated: estimated,
            remaining: utils.roundBasedOnThirdDecimalDigit(estimated - done),
            laborCodes: allLaborCodes.filter(
              (lc) => !EXCLUDED_LABOR_CODES.includes(lc.lc)
            ),
            totalFabricationHours,
          });
        } else {
          setFabricationData(null);
        }
      } catch (error) {
        setFabricationData(null);
        console.error("Error fetching fabrication data:", error);
      }
    };

    fetchChartData();
  }, []);

  // Placeholder data based on the screenshot
  const defaultData = {
    done: 100,
    estimated: 200,
    remaining: 100,
    laborCodes: [
      { lc: "01", done: 0, rem: 0 },
      { lc: "02", done: 0, rem: 0 },
      { lc: "03", done: 0, rem: 0 },
      { lc: "04", done: 0, rem: 0 },
      { lc: "05", done: 0, rem: 0 },
      { lc: "06", done: 0, rem: 0 },
      { lc: "07", done: 0, rem: 0 },
      { lc: "08", done: 0, rem: 0 },
      { lc: "09", done: 0, rem: 0 },
      { lc: "10", done: 0, rem: 0 },
      { lc: "11", done: 0, rem: 0 },
      { lc: "12", done: 0, rem: 0 },
      { lc: "13", done: 0, rem: 0 },
      { lc: "15", done: 0, rem: 0 },
    ],
    totalFabricationHours: {
      total: 0,
      laborCodes: [
        { lc: "14", done: 0, rem: 0 },
        { lc: "18", done: 0, rem: 0 },
      ],
    },
  };

  const finalFabricationData = fabricationData || defaultData;

  const {
    done = 0,
    estimated = 0,
    remaining = 0,
    laborCodes = [],
    totalFabricationHours = {
      total: 0,
      laborCodes: [],
    },
  } = finalFabricationData || {};

  const chartData = {
    datasets: [
      {
        data: [done, estimated, remaining],
        backgroundColor: ["#20297f", "#5965f8", "#b8b8b8"],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    cutout: "65%",
    events: [],
    plugins: { tooltip: { enabled: false }, legend: { display: false } },
  };

  const LcList = ({ items }) => (
    <div>
      <Row className="text-muted bg-soft-blue">
        <Col sm="3">LC</Col>
        <Col sm="4" className="pl-0 text-muted text-left">
          Done
        </Col>
        <Col className="pl-0 text-muted text-left">Rem.</Col>
      </Row>
      {items.map((item) => (
        <Row key={item.lc}>
          <Col sm="3">
            <strong>{item.lc}</strong>
          </Col>
          <Col sm="4" className="pl-0 text-muted">
            {item.done}
          </Col>
          <Col className="pl-0 text-muted">{item.rem}</Col>
        </Row>
      ))}
    </div>
  );

  const legendItems = [
    { label: "Done", value: `${done} hs`, color: "#20297f" },
    { label: "Estimated", value: `${estimated} hs`, color: "#5965f8" },
    { label: "Remaining", value: `${remaining} hs`, color: "#b8b8b8" },
  ];

  const columns = [
    laborCodes.slice(0, 6),
    laborCodes.slice(6, 12),
    laborCodes.slice(12, 18),
  ];

  return (
    <Card className="mb-0">
      <CardBody>
        <Row>
          <Col md="3">
            <div style={{ maxWidth: "180px", margin: "0 auto" }}>
              <Doughnut data={chartData} options={chartOptions} />
            </div>
            <Row className="mt-3">
              {legendItems.map((item) => (
                <Col key={item.label} className="pr-0">
                  <div className="d-flex">
                    <div
                      style={{
                        width: "5px",
                        backgroundColor: item.color,
                        marginRight: "8px",
                      }}
                      className="rounded"
                    />
                    <div>
                      <div className="text-muted small">{item.label}</div>
                      <strong>{item.value}</strong>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Col>
          <Col md="7" className="d-flex flex-column justify-content-center">
            <span
              className="position-absolute h-50 bg-light"
              style={{ width: 3, left: 0 }}
            ></span>
            <Row>
              {columns.map((col, i) => (
                <Col key={i} className="ml-3">
                  <LcList items={col} />
                </Col>
              ))}
            </Row>
            <span
              className="position-absolute h-50 bg-light"
              style={{ width: 3, right: -12 }}
            ></span>
          </Col>
          <Col
            md="2"
            className=" text-left d-flex flex-column justify-content-center"
          >
            <h5 className="text-muted pl-3">Total Fabrication Hours</h5>
            <h2 className="pb-3 pl-3">{totalFabricationHours.total}</h2>
            <div className="pl-4">
              <LcList items={totalFabricationHours.laborCodes} />
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default FabricationHoursChart;
